import ContentContainer from '@/components/containers/contentContainer'
import React, { useEffect, useRef, useState } from 'react'
import { api } from '@/utils/api';
import { useSelector } from 'react-redux';
import { Divider, Pagination } from '@mui/material';
import moment from 'moment';
import { Spinner } from '@/shared/spinner/spinner';
import Popup from '@/shared/popups/popupDetails';
import { toast } from "@/utils/appToast";
import { FilterI } from '@/types/interfaces';
import { useTranslation } from "react-i18next";
import JobPaymentSummarySection from '@/components/job/JobPaymentSummarySection';
import JobCommissionBreakdown from '@/components/job/JobCommissionBreakdown';
import { getPaymentSummary } from '@/utils/jobPaymentSummary';

const TransactionsList = () => {
  const { t, i18n } = useTranslation();
  const [tableData, setTableData] = useState<any>([]);
  const { user } = useSelector((state: { user }) => state.user || {});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [offset, setOffset] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const hasPageBeenRendered = useRef(false);
  const [filterValue, setFilterValue] = useState<FilterI>({
    text: '',
    page: offset,
    limit: pageSize,
    order: 'desc',
    clientName: '',
    workerName: '',
    status: ''
});
const getStatusConfig = (status: string) => {
  const statusConfigs = {
    'pending': {
      text: t('pending'),
      color: 'text-yellow-500',
      dot: 'bg-yellow-400',
    },
    'completed': {
      text: t('completed'),
      color: 'text-green-500',
      dot: 'bg-green-500',
    },
    'successfully_completed': {
      text: t('completed'),
      color: 'text-green-500',
      dot: 'bg-green-500',
    },
  };
  return statusConfigs[status] || statusConfigs['pending'];
};

  useEffect(() => {
    setIsLoading(true)
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const res = await api.getWorkerTransactions(user?._id, filterValue);
      if (res && res.success) {
        console.log("res", res.data)
        setTableData(res.data);
        hasPageBeenRendered.current = true;
      }
    } catch (err) {
      console.error("Failed to fetch jobs", err);
    } finally {
      setIsLoading(false);
    }
  }

 
  useEffect(() => {
    if( hasPageBeenRendered.current==true) {
    const delayDebounceFn = setTimeout(() => {
        fetchTransactions();
    }, 300);


    return () => clearTimeout(delayDebounceFn);
}
}, [filterValue]);

const handleChangePage = (_event: unknown, newPage: number) => {
    setIsLoading(true);
    setOffset(newPage);
    setFilterValue({ ...filterValue, page: newPage });
}

  const handleJobClick = async (jobId: string, netAmount: number) => {
    const job = await api.getJobById(jobId);
    if(job && job.success){
      setSelectedJob({...job.data, netAmount: netAmount});
      console.log("selectedJob", selectedJob)
      setShowPopup(true);
    }
    else{
      toast.error("No se pudieron obtener los detalles del trabajo");
    }
  }

        // Helper to get service name
        const getServiceName = (job: any) => {
          if (typeof job.service === 'object' && job.service?.name) return job.service.name;
          if (typeof job.service === 'string') return job.service;
          return '-';
        };




  return (
    <ContentContainer className='w-full min-w-0 m-0 bg-gray-100'>
      <Popup
        isOpen={showPopup}
        handleClose={() => setShowPopup(false)}
        className="bg-white"
        size="md"
  headerTitle={t('workDetails')}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={selectedJob && (
          <div className="p-4 w-full">
            {/* Details grid: label + value stacked in each cell */}
            <div className="grid lg:grid-cols-5 md:grid-cols-4 grid-cols-2 w-full gap-2 gap-y-4 text-sm font-semibold border-b border-gray-200 pb-2 mb-4">
              <div>
                <div className="text-sm text-black font-semibold mb-1">{t('clientName')}</div>
                <div className="truncate max-w-[120px] font-normal text-black" title={selectedJob?.user?.fullName || 'N/A'}>{selectedJob?.user?.fullName || 'N/A'}</div>
              </div>

              <div>
                <div className="text-sm text-black font-semibold mb-1">{t('service_provider')}</div>
                <div className="truncate max-w-[120px] font-normal text-black" title={selectedJob?.assignedWorker?.fullName || 'N/A'}>{selectedJob?.assignedWorker?.fullName || 'N/A'}</div>
              </div>

              <div className="col-span-full">
                <JobPaymentSummarySection
                  summary={getPaymentSummary(selectedJob)}
                  variant="worker"
                />
              </div>
              <JobCommissionBreakdown
                job={selectedJob}
                variant="worker"
                netAmountOverride={selectedJob?.netAmount}
              />

              <div>
                <div className="text-sm text-black font-semibold mb-1">{t('service_name')}</div>
                <div className="truncate max-w-[140px] font-normal text-black capitalize" title={getServiceName(selectedJob)}>{getServiceName(selectedJob)}</div>
              </div>
              <div>
                <div className="text-sm text-black font-semibold mb-1">{t('date')}</div>
                <div className="font-normal text-black">{moment(selectedJob?.updatedAt).format('DD MMM YYYY')}</div>
              </div>
              <div>
                <div className="text-sm text-black font-semibold mb-1">{t('status')}</div>
                <div className={`font-semibold ${getStatusConfig(selectedJob?.status).color} capitalize`}>{getStatusConfig(selectedJob?.status).text}</div>
              </div>
            </div>

            <Divider className='border-gray-400 my-4' />
            {/* Description */}
            <div className="mb-4">
              <div className="font-semibold mb-1">{t('description')}</div>
              <div className="text-gray-700 text-sm mb-5 capitalize">{selectedJob.description}</div>
            </div>
            <Divider className='border-gray-400 my-4' />
            {/* Attachments */}
            <div className="mb-4">
              <div className="font-semibold mb-1">{t('attachment')}</div>
              <div className="flex gap-2 h-16 w-16">
                {selectedJob?.imageUrls && selectedJob.imageUrls.length > 0 ? (
                  selectedJob?.imageUrls.map((url: string, idx: number) => (
                    <img key={idx} src={url} alt={`attachment-${idx}`} className="h-full w-full object-cover rounded" />
                  ))
                ) : (
                    <span className="text-gray-400 text-xs">{t('noAttachments')}</span>
                )}
              </div>
            </div>
         
          </div>
        )}
      />
      <div className='flex flex-col gap-3 w-full'>
  <h1 className='text-2xl text-black font-bold mb-5'>{t('paymentHistory')}</h1>
        <Spinner isLoading={isLoading} />
        {!isLoading && (
          <>
            <div className="bg-white rounded-xl w-full min-w-0">
              <div className="table-outer w-full min-w-0">
                <table className="default-table transactions-history-table w-full">
                  <thead>
                    <tr>
                      <th>{t('transactionId')}</th>
                      <th>{t('amount')}</th>
                      <th>{t('status')}</th>
                      <th>{t('workDetails')}</th>
                      <th>{t('date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tableData?.data || []).length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-gray-400 py-10">
                          {t('noDataFound')}
                        </td>
                      </tr>
                    ) : (
                      (tableData?.data || []).map((record: any) => {
                        const uidOrId = record.uid || record.id;
                        return (
                          <tr key={record.id}>
                            <td>
                              <span className="truncate block text-black" title={uidOrId}>
                                {uidOrId}
                              </span>
                            </td>
                            <td>
                              <span className="text-black">${record.netAmount}</span>
                            </td>
                            <td>
                              <span className={`text-black ${getStatusConfig(record.status).color}`}>
                                {getStatusConfig(record.status).text}
                              </span>
                            </td>
                            <td>
                              <span
                                className="text-darkBlue cursor-pointer"
                                onClick={() => handleJobClick(record.job, record.netAmount)}
                              >
                                {t('view_details')}
                              </span>
                            </td>
                            <td>
                              <span className="text-black">
                                {moment(record.updatedAt).format('DD/MM/YYYY')}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-center">

          
<Pagination
  count={tableData?.totalPages || 1}
  color="primary"
  variant="outlined"
  page={offset}
  onChange={handleChangePage}
  showFirstButton
  showLastButton
  siblingCount={0}
/>
</div>
          </>
        )}
      </div>
    </ContentContainer>
  )
}

export default TransactionsList
// 